if (!customElements.get('portrait-customizer')) {
  customElements.define('portrait-customizer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('[data-portrait-form]');
      if (!this.form) return;
      this.controller = new AbortController();
      this.previewMode = this.dataset.previewMode === 'true';
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.steps = [...this.querySelectorAll('[data-step-button]')];
      this.petPanels = [...this.querySelectorAll('[data-pet-panel]')];
      this.visited ??= new Set();
      this.submitting = false;
      try {
        const variants = JSON.parse(this.querySelector('[data-portrait-variants]')?.textContent || '[]');
        this.variants = Array.isArray(variants) ? variants : [];
      } catch {
        this.variants = [];
      }
      this.form.noValidate = true;
      this.classList.add('is-enhanced');
      const navigation = this.querySelector('.brass-navigation');
      if (navigation) navigation.hidden = false;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.handleClick(event), options);
      this.form.addEventListener('input', event => this.handleInput(event), options);
      this.form.addEventListener('change', event => this.handleInput(event), options);
      this.form.addEventListener('frame-upload-change', () => this.update(), options);
      this.form.addEventListener('submit', event => this.handleSubmit(event), options);
      this.form.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || event.isComposing || !event.target.matches('[data-pet-name]')) return;
        event.preventDefault();
        if (this.step < this.panels.length) {
          if (this.validate(this.step)) this.showStep(this.step + 1);
        } else {
          this.form.requestSubmit();
        }
      }, options);
      window.addEventListener('pageshow', () => {
        this.submitting = false;
        this.update();
      }, options);
      this.form.addEventListener('reset', () => {
        queueMicrotask(() => {
          if (!this.controller) return;
          this.visited.clear();
          this.submitting = false;
          this.querySelectorAll('frame-photo-upload').forEach(upload => upload.resetUploads?.());
          this.panels.forEach(panel => this.showError(Number(panel.dataset.panel), ''));
          this.update();
          this.showStep(1, false);
        });
      }, options);
      this.update();
      this.showStep(Number(this.dataset.step) || 1, false);
      queueMicrotask(() => { if (this.controller) this.update(); });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-step-button') || button.hasAttribute('data-edit')) {
        this.showStep(Number(button.dataset.stepButton || button.dataset.edit));
      } else if (button.hasAttribute('data-next')) {
        if (this.validate(this.step)) this.showStep(this.step + 1);
      } else if (button.hasAttribute('data-back')) {
        this.showStep(this.step - 1);
      }
    }

    handleInput(event) {
      if (event.target.closest('frame-photo-upload')) return;
      const field = event.target;
      if (field.matches('[data-no-items]') && field.checked) {
        this.querySelectorAll('[data-portrait-item]').forEach(item => { item.checked = false; });
      }
      if (field.matches('[data-portrait-item]') && field.checked) {
        const none = this.querySelector('[data-no-items]');
        if (none) none.checked = false;
      }
      const panel = field.closest('[data-panel]');
      if (panel) this.visited.add(Number(panel.dataset.panel));
      this.update();
      if (event.type === 'change' && field.matches('[data-portrait-template]:checked') && field.value !== 'custom') {
        const filename = field.dataset.templateThumbnail;
        if (!filename) return;
        const thumbnail = [...(this.closest('.product')?.querySelectorAll('[data-thumb]') || [])]
          .find(button => button.querySelector('img')?.getAttribute('src')?.split('?')[0].endsWith(filename));
        thumbnail?.click();
      }
    }

    showStep(step, focus = true) {
      this.step = Math.min(this.panels.length, Math.max(1, Number(step) || 1));
      this.dataset.step = String(this.step);
      this.visited.add(this.step);
      this.panels.forEach(panel => { panel.hidden = Number(panel.dataset.panel) !== this.step; });
      this.steps.forEach(button => {
        if (Number(button.dataset.stepButton) === this.step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      const fill = this.querySelector('[data-progress-fill]');
      if (fill) fill.style.width = `${this.step / this.panels.length * 100}%`;
      this.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(this.step));
      this.text('[data-progress-step]', this.message('stepTemplate', { step: this.step, total: this.panels.length }));
      this.text('[data-progress-remaining]', this.step === this.panels.length ? this.dataset.lastStep : this.message('remainingTemplate', { count: this.panels.length - this.step }));
      const back = this.querySelector('[data-back]');
      const next = this.querySelector('[data-next]');
      const finish = this.querySelector('[data-finish]');
      const add = this.querySelector('[data-add]');
      if (back) back.hidden = this.step === 1;
      if (next) next.hidden = this.step === this.panels.length;
      if (finish) finish.hidden = this.step !== this.panels.length;
      if (add) add.hidden = this.step !== this.panels.length;
      this.querySelectorAll('[data-submit-status]').forEach(status => { status.hidden = this.step !== this.panels.length; });
      this.updateCompletion();
      if (focus) {
        const panel = this.panels.find(item => Number(item.dataset.panel) === this.step);
        panel?.querySelector('[data-panel-title]')?.focus({ preventScroll: true });
        const navigation = this.querySelector('.brass-navigation');
        if (navigation && navigation.getBoundingClientRect().top < 0) {
          navigation.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }
    }

    get petCount() {
      const count = Number(this.querySelector('[data-pet-count]:checked')?.value);
      return [1, 2, 3].includes(count) ? count : 0;
    }

    get activePets() {
      return this.petPanels.filter(panel => Number(panel.dataset.petPanel) <= this.petCount);
    }

    get maxTabletopItems() {
      const maximum = Number(this.dataset.maxTabletopItems);
      return Number.isInteger(maximum) && maximum > 0 ? maximum : 8;
    }

    syncPets() {
      this.petPanels.forEach(panel => {
        const active = Number(panel.dataset.petPanel) <= this.petCount;
        panel.hidden = !active;
        if (panel.tagName === 'FIELDSET') panel.disabled = !active;
        panel.querySelectorAll('input, textarea, select, button').forEach(field => {
          if (!active) {
            if (!field.disabled) field.dataset.portraitInactive = '';
            field.disabled = true;
          } else if (field.hasAttribute('data-portrait-inactive')) {
            field.disabled = false;
            delete field.dataset.portraitInactive;
          }
        });
        const upload = panel.querySelector('frame-photo-upload');
        if (upload) {
          const sendLater = Boolean(upload.sendLater);
          upload.querySelectorAll('[data-upload-now] input, [data-upload-now] button').forEach(field => {
            field.disabled = !active || sendLater;
          });
          const laterProperty = upload.querySelector('[data-upload-later-property]');
          if (laterProperty) laterProperty.disabled = !active || !sendLater;
        }
      });
    }

    update() {
      this.syncPets();
      const template = this.querySelector('[data-portrait-template]:checked');
      const custom = template?.value === 'custom';
      const background = this.querySelector('[data-custom-background]');
      if (background) {
        background.disabled = !custom;
        background.required = custom;
        const container = background.closest('[data-custom-background-wrap]');
        if (container) container.hidden = !custom;
      }
      const templateProperty = this.querySelector('[data-template-property]');
      if (templateProperty) templateProperty.value = template?.dataset.templateName || '';
      const items = [...this.querySelectorAll('[data-portrait-item]:checked')].map(item => item.value);
      const noItems = Boolean(this.querySelector('[data-no-items]')?.checked);
      const atItemLimit = items.length >= this.maxTabletopItems;
      this.querySelectorAll('[data-portrait-item]').forEach(item => { item.disabled = atItemLimit && !item.checked; });
      this.text('[data-items-count]', this.message('itemsCountTemplate', { COUNT: items.length, MAX: this.maxTabletopItems }));
      this.dataset.itemsAtLimit = String(atItemLimit);
      if (items.length && noItems) this.querySelector('[data-no-items]').checked = false;
      const itemsValue = items.length ? items.join(', ') : noItems ? this.dataset.noItemsLabel : '';
      const itemsProperty = this.querySelector('[data-items-property]');
      if (itemsProperty) itemsProperty.value = itemsValue || '';
      const size = this.querySelector('[data-portrait-size]:checked');
      const production = this.querySelector('[data-production]:checked');
      const frame = this.querySelector('[data-portrait-frame]:checked');
      this.text('[data-summary-size]', size?.value || this.dataset.emptyLabel);
      this.text('[data-summary-template]', template?.dataset.templateName || this.dataset.emptyLabel);
      this.text('[data-summary-items]', itemsValue || this.dataset.emptyLabel);
      this.text('[data-summary-frame]', this.dataset.framesReady === 'true' ? frame?.value || this.dataset.emptyLabel : this.dataset.framesPending);
      const petDetails = this.activePets.map(panel => {
        const name = panel.querySelector('[data-pet-name]')?.value.trim();
        const upload = panel.querySelector('frame-photo-upload');
        const label = panel.querySelector('legend')?.textContent.trim() || '';
        if (upload?.sendLater) return `${name || label}: ${upload.dataset.laterLabel || ''}`;
        return name;
      }).filter(Boolean);
      const petChoice = this.querySelector('[data-pet-count]:checked');
      const petLabel = petChoice?.dataset.petLabel || petChoice?.closest('label')?.querySelector('strong')?.textContent.trim() || petChoice?.value;
      this.text('[data-summary-pets]', petLabel ? `${petLabel}${petDetails.length ? ` — ${petDetails.join('; ')}` : ''}` : this.dataset.emptyLabel);
      this.text('[data-summary-production]', production?.dataset.productionName || production?.value || this.dataset.emptyLabel);
      this.querySelectorAll('[data-size-preview]').forEach(preview => {
        const selected = preview.dataset.sizePreview === size?.value;
        preview.classList.toggle('is-selected', selected);
        preview.setAttribute('aria-hidden', String(!selected));
      });
      this.dataset.selectedSize = size?.value || '';
      const image = this.querySelector('[data-preview-template-image]');
      const source = template?.dataset.templateImage;
      if (image) {
        image.hidden = !source;
        if (source && image.getAttribute('src') !== source) image.src = source;
        if (!source) image.removeAttribute('src');
      }
      this.text('[data-preview-template-name]', template?.dataset.templateName || this.dataset.emptyLabel);
      this.updatePrice(size?.value, production?.value);
      this.updateCompletion();
      this.setSubmitting(this.submitting);
    }

    findVariant(size, pets, production) {
      return this.variants.find(variant => variant.size === size && Number(variant.pets) === Number(pets) && String(variant.production).toLowerCase() === String(production).toLowerCase());
    }

    updatePrice(size, production) {
      if (this.previewMode) return;
      this.variant = this.findVariant(size, this.petCount, production);
      const standard = this.findVariant(size, this.petCount, 'Standard');
      const base = this.findVariant(size, 1, 'Standard');
      const id = this.querySelector('[data-variant-id]');
      if (id) id.value = this.variant?.id || '';
      this.text('[data-total]', this.variant?.money || this.dataset.emptyLabel);
      this.text('[data-base-price]', base?.money || this.dataset.emptyLabel);
      const petExtra = standard && base ? Number(standard.price) - Number(base.price) : null;
      const rushExtra = this.variant && standard ? Number(this.variant.price) - Number(standard.price) : null;
      this.text('[data-pet-extra]', petExtra === null ? this.dataset.emptyLabel : this.money(petExtra));
      this.text('[data-rush-extra]', rushExtra === null ? this.dataset.emptyLabel : this.money(rushExtra));
      const petRow = this.querySelector('[data-pet-extra-row]');
      const rushRow = this.querySelector('[data-rush-extra-row]');
      if (petRow) petRow.hidden = !petExtra;
      if (rushRow) rushRow.hidden = String(production).toLowerCase() !== 'rush';
      const price = this.closest('.product')?.querySelector('[data-price]');
      if (price && this.variant) price.textContent = this.variant.money;
      if (this.variant) this.dispatchEvent(new CustomEvent('seremori:variant-change', { bubbles: true, detail: this.variant }));
    }

    money(value) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency }).format(value / 100);
      } catch {
        return this.dataset.emptyLabel || '';
      }
    }

    updateCompletion() {
      this.steps.forEach(button => {
        const step = Number(button.dataset.stepButton);
        const complete = this.visited.has(step) && !this.issueFor(step) && (step !== 4 || this.dataset.framesReady === 'true');
        button.classList.toggle('is-complete', complete);
        const status = button.querySelector('[data-step-status]');
        if (status) status.textContent = complete ? ` — ${this.dataset.completeLabel || ''}` : '';
        const error = this.querySelector(`[data-error="${step}"]`);
        if (error && !error.hidden) this.showError(step, this.issueFor(step)?.message || '');
      });
    }

    issueFor(step, final = false) {
      const issue = (key, field, values) => ({ message: this.message(key, values), field });
      if (step === 1 && !this.querySelector('[data-portrait-size]:checked')) return issue('sizeRequired', this.querySelector('[data-portrait-size]'));
      if (step === 2) {
        const template = this.querySelector('[data-portrait-template]:checked');
        if (!template) return issue('templateRequired', this.querySelector('[data-portrait-template]'));
        const background = this.querySelector('[data-custom-background]');
        if (template.value === 'custom' && (!background?.value.trim() || !background.validity.valid)) return issue('backgroundRequired', background);
      }
      if (step === 3) {
        const items = this.querySelectorAll('[data-portrait-item]:checked');
        if (items.length > this.maxTabletopItems) return issue('itemsLimitError', items[this.maxTabletopItems], { COUNT: items.length, MAX: this.maxTabletopItems });
        if (!items.length && !this.querySelector('[data-no-items]:checked')) return issue('itemsRequired', this.querySelector('[data-portrait-item], [data-no-items]'));
      }
      if (step === 4) {
        if (this.dataset.framesReady !== 'true') return final ? issue('framesPending', this.panels[3]?.querySelector('[data-panel-title]')) : null;
        if (!this.querySelector('[data-portrait-frame]:checked')) return issue('frameRequired', this.querySelector('[data-portrait-frame]'));
      }
      if (step === 5) {
        if (!this.petCount) return issue('petCountRequired', this.querySelector('[data-pet-count]'));
        for (const panel of this.activePets) {
          const name = panel.querySelector('[data-pet-name]');
          const values = { number: panel.dataset.petPanel };
          if (name && (name.required && !name.value.trim() || !name.validity.valid)) return issue('petNameRequired', name, values);
          const upload = panel.querySelector('frame-photo-upload');
          if (!upload?.sendLater && !upload?.files?.length) return { message: upload?.errorMessage || this.message('petPhotoRequired', values), field: upload?.querySelector('[data-upload-picker]'), upload };
        }
      }
      if (step === 6) {
        if (!this.querySelector('[data-production]:checked')) return issue('productionRequired', this.querySelector('[data-production]'));
        if (!this.previewMode) {
          if (!this.variant?.available || !this.variant.id) return issue('variantUnavailable', this.querySelector('[data-production]'));
        }
      }
      return null;
    }

    showError(step, message, field) {
      const error = this.querySelector(`[data-error="${step}"]`);
      if (error) {
        error.textContent = message || '';
        error.hidden = !message;
      }
      const panel = this.panels.find(item => Number(item.dataset.panel) === step);
      panel?.querySelectorAll('[aria-invalid="true"]').forEach(input => {
        if (!input.closest('frame-photo-upload')) input.removeAttribute('aria-invalid');
      });
      if (message && field) field.setAttribute('aria-invalid', 'true');
    }

    validate(step, final = false) {
      this.syncPets();
      const issue = this.issueFor(step, final);
      this.showError(step, issue?.message, issue?.field);
      if (issue) {
        this.showStep(step, false);
        this.showError(step, issue.message, issue.field);
        issue.upload?.validate?.();
        issue.field?.focus();
      }
      return !issue;
    }

    handleSubmit(event) {
      if (this.previewMode) {
        event.preventDefault();
        this.syncPets();
        if (this.step < this.panels.length) {
          if (this.validate(this.step)) this.showStep(this.step + 1);
        } else {
          this.update();
          this.querySelector('[data-preview-notice]')?.focus({ preventScroll: true });
        }
        return;
      }
      this.syncPets();
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      if (this.step < this.panels.length) {
        event.preventDefault();
        if (this.validate(this.step)) this.showStep(this.step + 1);
        return;
      }
      this.update();
      for (let step = 1; step <= this.panels.length; step++) {
        if (!this.validate(step, true)) {
          event.preventDefault();
          return;
        }
      }
      if (event.defaultPrevented) return;
      this.setSubmitting(true);
      queueMicrotask(() => {
        if (event.defaultPrevented && this.controller) this.setSubmitting(false);
      });
    }

    setSubmitting(submitting) {
      if (this.previewMode) {
        this.submitting = false;
        this.form.removeAttribute('aria-busy');
        this.text('[data-submit-status]', '');
        return;
      }
      this.submitting = submitting;
      if (submitting) this.form.setAttribute('aria-busy', 'true');
      else this.form.removeAttribute('aria-busy');
      const unavailable = !this.variant?.available || !this.variant.id;
      const pending = this.dataset.framesReady !== 'true';
      const add = this.querySelector('[data-add]');
      if (add) {
        add.disabled = submitting || pending || unavailable;
        add.textContent = submitting ? this.dataset.addingLabel : this.dataset.addLabel;
      }
      this.text('[data-submit-status]', submitting ? this.dataset.addingLabel : pending ? this.dataset.framesPending : unavailable ? this.dataset.variantUnavailable : this.dataset.submitReady);
      this.querySelectorAll('[data-submit-status]').forEach(status => { status.hidden = this.step !== this.panels.length; });
    }

    message(key, values = {}) {
      return Object.entries(values).reduce((message, [name, value]) => message.replaceAll(`[${name}]`, String(value)), this.dataset[key] || '');
    }

    text(selector, value) {
      this.querySelectorAll(selector).forEach(element => { element.textContent = value || ''; });
    }
  });
}
